import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Container, Paper, useMediaQuery, useTheme } from "@mui/material";
import {
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import { useMutation, useQuery } from "@tanstack/react-query";

import { AuthLibMSALTokenAcquisitionError, useMSALPayload } from "@initia-digital/auth-client-utils";
import { BasicLoader, GeneralChip, JobTitleText, TextButton, Typography, useSnackbar } from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";
import { ISalesforceJob } from "@initia-digital/types/job";

import { useWorkAndSafetyStore } from "../../../store";
import { fetchJobDetail, generateAITags } from "../../../api/query";
import { postPlan } from "../../../api/mutate";
import { IPlan } from "@initia-digital/types/work-and-safety";
import { BasicAppBarPage } from "../../../layouts/BasicAppBarPage";
import { TwoColumnToSingleColumn } from "../../../legacy/Layout";
import { PlanStage } from "../../../legacy/Plan";
import PlanNavigation from "../../../legacy/PlanNavigation";
import { CONFIG } from "../../../config/const";
import { EmergencyDirectiveBanner } from "../../../legacy/EmergencyDirectiveBanner";

const { PLAN_STEPS, SF_URL } = CONFIG;

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });


const CreatePlanDetail = () => {
    // --------------------------------------------
    const [aiTagGenerationLoading, setAiTagGenerationLoading] = useState<boolean>(false);
    const { editPlanState, resetEditPlanState, updateEditPlanState } = useWorkAndSafetyStore();

    const navigate = useNavigate();
    const { jobId } = useParams();

    const [searchParams] = useSearchParams();
    const stage = searchParams.get('stage') ?? PLAN_STEPS[0].value;
    let activeStepIndex = PLAN_STEPS.findIndex(step => step.value === stage);
    activeStepIndex = activeStepIndex !== -1 ? activeStepIndex : 0;
    const previousStepIndex = activeStepIndex > 0 ? activeStepIndex - 1 : PLAN_STEPS.length - 1;
    const nextStepIndex = activeStepIndex < (PLAN_STEPS.length - 1) ? activeStepIndex + 1 : 0;

    // MUI HOOKS
    // ---------------------
    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
    const { queueSnackbar } = useSnackbar();

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();

    // JOB DETAIL
    // ---------------------
    const { data: jobDetail, isLoading: isJobDetailLoading } = useQuery<Partial<ISalesforceJob>>({
        queryKey: ['job', jobId],
        queryFn: () => fetchJobDetail(authDetail, jobId!),
        enabled: baseDefinedAndNotNullNarrowing(jobId),
        refetchOnWindowFocus: true,
        retry: 2
    });
    const { jobUUID, jobName, client } = jobDetail ?? { jobUUID: undefined, jobName: undefined, client: undefined };

    // MUTATIONS
    // ---------------------
    const createPlan = useMutation({
        mutationFn: postPlan,
        onSuccess: (_, variables) => {
            // ---------------------
            resetEditPlanState(jobId!);
            queueSnackbar(`Plan created for ${variables.plan.jobId}`, 'success', 'Success', 3000, '1rem');
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error saving plan', { error });
            }
            logger.error('Error saving plan');
            queueSnackbar('Plan could not be created', 'error', 'Error saving plan', 3000, '1rem');
        }
    });

    // Reset plan state when job changes
    // ---------------------
    useEffect(() => {
        if (baseDefinedAndNotNullNarrowing(jobId)) {
            // Only reset if the current plan state is for a different job
            if (editPlanState.jobId !== jobId || editPlanState.id !== '') {
                // ---------------------
                resetEditPlanState(jobId!);
            }
        }
    }, [jobId, editPlanState.jobId, editPlanState.id, resetEditPlanState]);

    // EVENT HANDLERS
    // ---------------------
    const handleFormNav = (type: 'NEXT' | 'BACK') => {
        // ---------------------
        let stepIdx = activeStepIndex;
        if (type === 'NEXT') {
            stepIdx = nextStepIndex;
        }
        else if (type === 'BACK') {
            stepIdx = previousStepIndex;
        }
        navigate(`/job/${jobId}/plan?stage=${PLAN_STEPS[stepIdx]!.value}`);
    }

    const handleSave = async (directPlanProvision?: IPlan) => {
        // ----------------------------------------------

        if (!baseDefinedAndNotNullNarrowing(jobId)) {
            throw new Error('Job ID could not be picked up');
        }

        // DEFAULT behaviour is to use the editPlanState
        let planCreate = editPlanState;
        if (baseDefinedAndNotNullNarrowing(directPlanProvision)) {
            // -------------------------------------
            planCreate = directPlanProvision;
        }

        const res = await createPlan.mutateAsync({
            plan: {
                ...planCreate,
                jobId
            },
            authDetail
        });

        navigate(`/job/${jobId}/edit-plan/${res.id}?stage=${PLAN_STEPS[activeStepIndex]!.value}`);
    }

    const handleSaveAndNext = async () => {
        // ----------------------------------------------

        if (!baseDefinedAndNotNullNarrowing(jobId)) {
            throw new Error('Job ID could not be picked up');
        }

        const res = await createPlan.mutateAsync({
            plan: {
                ...editPlanState,
                jobId
            },
            authDetail
        });

        navigate(`/job/${jobId}/edit-plan/${res.id}?stage=${PLAN_STEPS[nextStepIndex]!.value}`);
    }

    const handleAiTagGeneration = async () => {
        // --------------------------------------
        setAiTagGenerationLoading(true);
        const res = await generateAITags(authDetail, {
            maxTags: 5,
            outputSpec: encodeURIComponent(`specific type of geotechnical work being done in this description`),
            temperature: 0.3
        },
            {
                content: editPlanState.description
            }
        );

        if ('tags' in res && baseDefinedAndNotNullNarrowing(res?.tags) && res.tags.length > 0) {
            // ---------------------------------------------
            const uniqueTags = res.tags.filter((tag: string) => editPlanState.objectives.indexOf(tag) === -1);
            updateEditPlanState({
                ...editPlanState,
                objectives: [...editPlanState.objectives, ...uniqueTags]
            });
        }
        setAiTagGenerationLoading(false);
    }



    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="xl" sx={{ px: !isSmallerThanMd ? "25px" : "0px", py: "10px" }}>
                <EmergencyDirectiveBanner />
                <Box mx="10px" my="5px" height="100%">
                    <Paper sx={{ height: '100%', width: '100%' }} elevation={0}>
                        <Box
                            display="flex" flexDirection="row" justifyContent="center"
                            pt={{ xs: '10px', sm: '15px', md: '20px' }}
                            px={{ xs: '5px', md: '20px' }}
                            height="100%"
                        >
                            <Box display="flex" flexDirection="column" sx={{ width: '100%' }} >
                                <TwoColumnToSingleColumn
                                    leftChildren={
                                        <Box display="flex" flexDirection="column">
                                            <Box>
                                                <Box display="flex" mb="10px">
                                                    <TextButton
                                                        color={theme.palette.primary.contrastText}
                                                        hoverBackgroundColor={theme.palette.primary.main}
                                                        onClick={() => {
                                                            navigate(`/job/${jobId}`)
                                                        }}
                                                        startIcon={<ArrowBackIcon />}
                                                    >
                                                        {`Back to ${jobId}`}
                                                    </TextButton>
                                                </Box>
                                                {
                                                    isJobDetailLoading
                                                    &&
                                                    <BasicLoader
                                                        size={50}
                                                    />
                                                }
                                                {
                                                    baseDefinedAndNotNullNarrowing(jobDetail)
                                                    &&
                                                    <Box
                                                        px="20px"
                                                        display="flex"
                                                        flexDirection="column"
                                                    >
                                                        <JobTitleText
                                                            jobId={jobId}
                                                            jobName={jobName ?? '...'}
                                                            isSelected={true}
                                                        />
                                                        <Box>
                                                            <Typography
                                                                fontSize="0.8rem"
                                                                fontWeight={300}
                                                                color={theme.palette.primary.contrastText}
                                                            >
                                                                {client}
                                                            </Typography>
                                                        </Box>
                                                        <Box py="5px">
                                                            <GeneralChip
                                                                label="Open in Salesforce"
                                                                href={`${SF_URL}/${jobUUID}`}
                                                                size="vsmall"
                                                                backgroundColor={"#3bbff7"}
                                                                color="white"
                                                                clickable
                                                                newTab={true}
                                                            />
                                                        </Box>
                                                    </Box>
                                                }
                                                <PlanNavigation
                                                    stage={stage}
                                                    handleFormNav={handleFormNav}
                                                />
                                            </Box>
                                        </Box>}
                                    gridLeftAlignment='flexStart'
                                    rightChildren={<Box display="flex" justifyContent="center">
                                        <PlanStage
                                            handleSave={handleSave}
                                            handleSaveAndNext={handleSaveAndNext}
                                            aiTagGenerationLoading={aiTagGenerationLoading}
                                            setAiTagGenerationLoading={setAiTagGenerationLoading}
                                            handleAiTagGeneration={handleAiTagGeneration}
                                        />
                                    </Box>}
                                    gridRightAlignment='flexStart'
                                    colLeft={4}
                                    colRight={8}
                                />
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Container>
        </BasicAppBarPage>
    )
}

export default CreatePlanDetail;